import { memo, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import GlobeIcon from '../../assets/icons/globe.svg?react';
import { I18N_LIST, LOCALE_LIST } from '@link/share/constants';
import styles from './header.module.css';

const LocaleView = () => {
  const [openMenu, setOpenMenu] = useState(false);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLocale = useMemo(
    () => I18N_LIST.find(locale => locale.value === i18n.language)?.label,
    [i18n.language]
  );

  const changeLocale = useCallback(
    (locale: string) => {
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const currentLocale = pathSegments[0];

      let newPath = '';
      if (LOCALE_LIST.includes(currentLocale)) {
        newPath = `/${locale}/${pathSegments.slice(1).join('/')}`;
      } else {
        newPath = `/${locale}${location.pathname}`;
      }

      navigate(newPath);
      setOpenMenu(false);
    },
    [navigate, location.pathname]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.localeDropdown} ref={dropdownRef}>
      <button
        className={styles.button}
        onClick={() => setOpenMenu(visible => !visible)}
      >
        <GlobeIcon className={styles.icon} />
        <span className={styles.text}>{currentLocale}</span>
      </button>
      <div
        className={`${styles.dropdownMenu} ${openMenu ? styles.dropdownMenuOpen : ''}`}
      >
        {I18N_LIST.map(locale => (
          <button
            key={locale.value}
            className={`${styles.dropdownButton} ${
              i18n.language === locale.value ? styles.dropdownButtonActive : ''
            }`}
            onClick={() => changeLocale(locale.value)}
          >
            {locale.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default memo(LocaleView);
